import { TypedDocumentNode, gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled, { CSSProperties } from 'styled-components';

import { Link } from '@sorare/routing';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyM, HeadlineM } from '@sorare/core/src/atoms/typography';
import { Box, LabelPosition } from '@sorare/core/src/atoms/ui/Box';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { ANY_SPORT_MY_CARDS_ESSENCE } from '@sorare/core/src/constants/__generated__/routes';
import {
  AUCTION_MARKET_URL,
  INSTANT_BUY_URL,
} from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useMainColor } from '@sorare/core/src/hooks/useMainColor';
import {
  RgbString,
  getTextColorFromBackgroundColor,
  isLightColor,
} from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useHasInstantBuyCards } from 'hooks/useHasInstantBuyCards';

import type { PromotionalEventBlock_PromotionalEventInterface } from './__generated__/index.graphql';

const Wrapper = styled(Box)`
  --border-size: 2px;
  border-radius: calc(var(--double-unit) + var(--border-size));
  border: var(--border-size) solid rgb(var(--main-color, --c-rgb-blue-500));
  background: var(--background) center / cover;

  position: relative;
  color: var(--text-color);
`;

const Logo = styled.img`
  height: 150px;
  object-fit: contain;
  max-width: 100%;
  margin: auto;
  display: block;
`;

const HiddenOnMobile = styled(Vertical).attrs({ gap: 2 })`
  opacity: 0;
  position: absolute;
  inset: 0;

  @media ${tabletAndAbove} {
    display: flex;
    opacity: 1;
    position: static;
    inset: auto;
  }
`;

type Props = {
  promotionalEvent: PromotionalEventBlock_PromotionalEventInterface;
};

export const PromotionalEventBlock = ({ promotionalEvent }: Props) => {
  const { sport } = useSportContext();

  const { pictureUrl, logoUrl, blogUrl, slug, algoliaFilters } =
    promotionalEvent;
  const instantBuyHasResults = useHasInstantBuyCards(sport, algoliaFilters);

  const prominentColor = useMainColor(promotionalEvent.pictureUrl ?? undefined);

  const color = isLightColor(prominentColor)
    ? prominentColor
    : (prominentColor
        .split(',')
        .map(x =>
          // make color lighter
          Math.min(255, parseInt(x, 10) * 2)
        )
        .join(', ') as RgbString);

  const isColorLight = isLightColor(color);

  if (!sport) {
    return null;
  }

  let to: string;
  let cta: ReactNode;

  if (isType(promotionalEvent, 'CardShardThresholdsPromotionalEvent')) {
    to = generateSportPath(ANY_SPORT_MY_CARDS_ESSENCE, { sport });
    cta = (
      <FormattedMessage
        id="PromotionalEvent.cta.essence"
        defaultMessage="Craft now"
      />
    );
  } else {
    const tab = instantBuyHasResults
      ? INSTANT_BUY_URL[sport]
      : AUCTION_MARKET_URL[sport];
    to = generatePath(
      `${tab}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${slug}#market`
    );
    cta = (
      <FormattedMessage
        id="PromotionalEvent.cta.text"
        defaultMessage="Shop now"
      />
    );
  }

  return (
    <Wrapper
      style={
        {
          '--background': `url(${pictureUrl})`,
          '--main-color': color,
          '--text-color': getTextColorFromBackgroundColor(color),
        } as CSSProperties
      }
      labelProps={{
        value: promotionalEvent.name,
        color: getTextColorFromBackgroundColor(color),
        bgColor: 'rgb(var(--main-color, --c-rgb-blue-500))',
        position: LabelPosition.LEFT,
      }}
    >
      <Vertical>
        <div>
          <Logo src={logoUrl!} alt="" />
        </div>
        <div>
          <Chip color={isColorLight ? 'grey' : 'yellow'}>
            <FontAwesomeIcon icon={faClock} />
            <TimeLeft time={promotionalEvent.endDate} />
          </Chip>
        </div>
        <HeadlineM>{promotionalEvent.title}</HeadlineM>
        <HiddenOnMobile>
          <BodyM style={{ whiteSpace: 'pre-line' }}>
            {promotionalEvent.description.replace('\\n', '\n')}
          </BodyM>
          <Horizontal>
            <LinkOverlay as={Link} to={to}>
              <Button
                asSpan
                size="small"
                color={isColorLight ? 'tertiary' : 'secondary'}
              >
                {cta}
              </Button>
            </LinkOverlay>
            {blogUrl && (
              <LinkOther
                as={Button}
                href={blogUrl}
                size="small"
                color={isColorLight ? 'secondary' : 'quaternary'}
              >
                <FormattedMessage {...glossary.learnMore} />
              </LinkOther>
            )}
          </Horizontal>
        </HiddenOnMobile>
      </Vertical>
    </Wrapper>
  );
};

PromotionalEventBlock.fragments = {
  PromotionalEventInterface: gql`
    fragment PromotionalEventBlock_PromotionalEventInterface on PromotionalEventInterface {
      slug
      name
      title
      description
      pictureUrl
      logoUrl
      blogUrl
      endDate
      algoliaFilters
    }
  ` as TypedDocumentNode<PromotionalEventBlock_PromotionalEventInterface>,
};
